import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useAutosave — hook genérico de auto-guardado con debounce y estado visible.
 *
 * Estados: idle · saving · saved · error
 * Alineado con Etapa 4 §2.4 (formularios) y Etapa 2 §2.4 (auto-guardado).
 *
 * @param {(patch:object) => Promise<any>} saveFn función que persiste el patch
 * @param {number} [delay=800] ms de debounce
 */
export default function useAutosave(saveFn, delay = 800) {
  const [state, setState] = useState("idle");
  const [error, setError] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const timer = useRef(null);
  const pending = useRef({});

  const flush = useCallback(async () => {
    if (Object.keys(pending.current).length === 0) return;
    const patch = pending.current;
    pending.current = {};
    setState("saving");
    setError(null);
    try {
      await saveFn(patch);
      setState("saved");
      setLastSavedAt(new Date());
    } catch (e) {
      setState("error");
      setError(e);
    }
  }, [saveFn]);

  const schedule = useCallback(
    (patch) => {
      pending.current = { ...pending.current, ...patch };
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(flush, delay);
    },
    [flush, delay]
  );

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  return { state, error, lastSavedAt, schedule, flush };
}
